import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "../context/CustomerAuthContext";

/**
 * Handles the Emergent-managed Google Auth callback.
 * Reads #session_id=... from the URL fragment, exchanges it for a customer JWT
 * via the backend, then redirects to /account (or the intended destination).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { googleSession } = useCustomerAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Read intended destination from sessionStorage (set before login redirect)
    const intended = sessionStorage.getItem("post_google_redirect") || "/account";
    sessionStorage.removeItem("post_google_redirect");

    if (!sessionId) {
      setError("No Google session found. Please try signing in again.");
      setTimeout(() => navigate("/login", { replace: true }), 1600);
      return;
    }

    googleSession(sessionId)
      .then((user) => {
        toast.success(`Welcome, ${user.name?.split(" ")[0] || "friend"}`);
        // Clear the fragment then navigate
        window.history.replaceState(null, "", window.location.pathname);
        navigate(intended, { replace: true });
      })
      .catch(() => {
        setError("We couldn't complete Google sign-in. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 1800);
      });
  }, [googleSession, navigate]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="google-auth-callback">
      <div className="text-center max-w-md">
        <p className="eyebrow">One moment</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3">
          {error ? "Something went wrong" : "Signing you in…"}
        </h1>
        <p className="text-[#4A4A4A] mt-5 text-[14px] leading-relaxed">
          {error || "Bridging your Google account with Asvasa."}
        </p>
        {!error && (
          <div className="mt-8 flex justify-center">
            <span className="w-2 h-2 rounded-full bg-[#1A1A1A] animate-pulse" />
            <span className="w-2 h-2 rounded-full bg-[#1A1A1A] animate-pulse ml-2" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 rounded-full bg-[#1A1A1A] animate-pulse ml-2" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>
    </div>
  );
}
